/**
 * Standard emoji catalog for the composer's picker, grouped into the same
 * eight categories Telegram Web A uses. Only well-known single-codepoint (or
 * simple variation-selector) glyphs are listed so every entry resolves to an
 * Apple img-apple-64 sprite via emojiSrc(); ZWJ-heavy sequences are omitted.
 */

export type EmojiCategory = {
  key: string;
  label: string;
  icon: string;
  emojis: string[];
};

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    key: "smileys",
    label: "Smileys & People",
    icon: "😀",
    emojis: [
      "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣",
      "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰",
      "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜",
      "🤪", "🤨", "🧐", "🤓", "😎", "🥳", "🤩", "😏",
      "😒", "😞", "😔", "😟", "😕", "🙁", "😣", "😖",
      "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡",
      "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨", "😰",
      "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥", "😶",
      "😐", "😑", "😬", "🙄", "😮", "😲", "🥱", "😴",
      "🤤", "😪", "😵", "🤐", "🥴", "🤢", "🤮", "🤧",
      "😷", "🤒", "🤕", "😈", "👿", "👻", "💀", "👽",
      "🤖", "💩", "👍", "👎", "👌", "✌️", "🤞", "🤟",
      "🤘", "🤙", "👈", "👉", "👆", "👇", "☝️", "✋",
      "🤚", "🖐️", "🖖", "👋", "🤝", "🙏", "💪", "👏",
      "🙌", "👐", "🤲", "🤦", "🤷", "💅", "👀", "🧠",
    ],
  },
  {
    key: "animals",
    label: "Animals & Nature",
    icon: "🐻",
    emojis: [
      "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼",
      "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔",
      "🐧", "🐦", "🐤", "🦆", "🦅", "🦉", "🦇", "🐺",
      "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌", "🐞",
      "🐜", "🕷️", "🦂", "🐢", "🐍", "🦎", "🐙", "🦑",
      "🦐", "🦀", "🐡", "🐠", "🐟", "🐬", "🐳", "🐋",
      "🦈", "🐊", "🐅", "🐆", "🦓", "🦍", "🐘", "🦏",
      "🐪", "🐫", "🦒", "🐄", "🐎", "🐖", "🐑", "🐐",
      "🦌", "🐕", "🐈", "🐓", "🦃", "🕊️", "🐇", "🐁",
      "🌵", "🎄", "🌲", "🌳", "🌴", "🌱", "🌿", "☘️",
      "🍀", "🍃", "🍂", "🍁", "🌾", "🌺", "🌻", "🌹",
      "🌷", "🌸", "💐", "🍄", "🌰", "🌎", "🌙", "⭐️",
    ],
  },
  {
    key: "food",
    label: "Food & Drink",
    icon: "🍔",
    emojis: [
      "🍏", "🍎", "🍐", "🍊", "🍋", "🍌", "🍉", "🍇",
      "🍓", "🍈", "🍒", "🍑", "🥭", "🍍", "🥥", "🥝",
      "🍅", "🍆", "🥑", "🥦", "🥒", "🌶️", "🌽", "🥕",
      "🥔", "🍠", "🥐", "🍞", "🥖", "🥨", "🧀", "🥚",
      "🍳", "🥞", "🥓", "🥩", "🍗", "🍖", "🌭", "🍔",
      "🍟", "🍕", "🥪", "🌮", "🌯", "🥗", "🥘", "🍝",
      "🍜", "🍲", "🍛", "🍣", "🍱", "🍚", "🍙", "🍘",
      "🍢", "🍡", "🍧", "🍨", "🍦", "🍰", "🎂", "🍮",
      "🍭", "🍬", "🍫", "🍿", "🍩", "🍪", "🥛", "🍼",
      "☕️", "🍵", "🍶", "🍺", "🍻", "🥂", "🍷", "🥃",
      "🍸", "🍹", "🍾",
    ],
  },
  {
    key: "activity",
    label: "Activity",
    icon: "⚽️",
    emojis: [
      "⚽️", "🏀", "🏈", "⚾️", "🥎", "🎾", "🏐", "🏉",
      "🎱", "🏓", "🏸", "🥅", "🏒", "🏑", "🏏", "⛳️",
      "🏹", "🎣", "🥊", "🥋", "⛸️", "🎿", "⛷️", "🏂",
      "🏋️", "🤼", "🤸", "⛹️", "🤺", "🤾", "🏌️", "🏇",
      "🧘", "🏄", "🏊", "🤽", "🚣", "🚴", "🚵", "🎽",
      "🏅", "🎖️", "🥇", "🥈", "🥉", "🏆", "🎗️", "🎫",
      "🎟️", "🎪", "🎭", "🎨", "🎬", "🎤", "🎧", "🎼",
      "🎹", "🥁", "🎷", "🎺", "🎸", "🎻", "🎲", "🎯",
      "🎳", "🎮", "🎰", "🧩",
    ],
  },
  {
    key: "travel",
    label: "Travel & Places",
    icon: "🚗",
    emojis: [
      "🚗", "🚕", "🚙", "🚌", "🚎", "🏎️", "🚓", "🚑",
      "🚒", "🚐", "🚚", "🚛", "🚜", "🛴", "🚲", "🛵",
      "🏍️", "🚨", "🚔", "🚍", "🚘", "🚖", "🚡", "🚠",
      "🚟", "🚃", "🚋", "🚞", "🚝", "🚄", "🚅", "🚈",
      "🚂", "🚆", "🚇", "🚊", "🚉", "✈️", "🛫", "🛬",
      "🛩️", "🚁", "🛸", "🚀", "🛰️", "⛵️", "🚤", "🛳️",
      "⛴️", "🚢", "⚓️", "⛽️", "🚧", "🗺️", "🗿", "🗽",
      "🗼", "🏰", "🏯", "🏟️", "🎡", "🎢", "🎠", "⛲️",
      "🏖️", "🏝️", "🏜️", "🌋", "⛰️", "🏔️", "🗻", "🏕️",
      "⛺️", "🏠", "🏡", "🏢", "🏬", "🏣", "🏥", "🏦",
      "🏨", "🏪", "🏫", "🏛️", "⛪️", "🕌", "🕍", "⛩️",
      "🌁", "🌃", "🌉", "🌆", "🌇", "🌌", "🌠", "🎇",
      "🎆", "🌈",
    ],
  },
  {
    key: "objects",
    label: "Objects",
    icon: "💡",
    emojis: [
      "⌚️", "📱", "💻", "⌨️", "🖥️", "🖨️", "🖱️", "💽",
      "💾", "💿", "📀", "📷", "📸", "📹", "🎥", "📞",
      "☎️", "📟", "📠", "📺", "📻", "🎙️", "⏰", "⏳",
      "⌛️", "📡", "🔋", "🔌", "💡", "🔦", "🕯️", "🧯",
      "💸", "💵", "💴", "💶", "💷", "💰", "💳", "💎",
      "⚖️", "🔧", "🔨", "🛠️", "🔩", "⚙️", "🧲", "🔫",
      "💣", "🔪", "🛡️", "🚬", "⚰️", "🏺", "🔮", "📿",
      "💈", "🔭", "🔬", "💊", "💉", "🌡️", "🚽", "🚿",
      "🛁", "🧴", "🧷", "🧹", "🧺", "🧻", "🧼", "🧽",
      "🔑", "🗝️", "🚪", "🛋️", "🛏️", "🖼️", "🛍️", "🛒",
      "🎁", "🎈", "🎀", "🎊", "🎉", "🏮", "✉️", "📩",
      "📨", "📧", "📦", "📫", "📝", "✏️", "✒️", "🖊️",
      "🖌️", "🖍️", "📁", "📂", "📅", "📆", "📈", "📉",
      "📊", "📋", "📌", "📍", "📎", "📏", "📐", "✂️",
      "🗑️", "🔒", "🔓", "🔐", "📖", "📚", "🔖", "📄",
      "📰",
    ],
  },
  {
    key: "symbols",
    label: "Symbols",
    icon: "❤️",
    emojis: [
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍",
      "🤎", "💔", "❣️", "💕", "💞", "💓", "💗", "💖",
      "💘", "💝", "💟", "☮️", "✝️", "☪️", "🕉️", "☸️",
      "✡️", "🔯", "🕎", "☯️", "☦️", "🛐", "⛎", "♈️",
      "♉️", "♊️", "♋️", "♌️", "♍️", "♎️", "♏️", "♐️",
      "♑️", "♒️", "♓️", "🆔", "⚛️", "☢️", "☣️", "📴",
      "📳", "✴️", "🆚", "💮", "❌", "⭕️", "🛑", "⛔️",
      "📛", "🚫", "💯", "💢", "♨️", "🔞", "📵", "🚭",
      "❗️", "❕", "❓", "❔", "‼️", "⁉️", "🔅", "🔆",
      "⚠️", "🚸", "🔱", "⚜️", "🔰", "♻️", "✅", "❇️",
      "✳️", "❎", "🌐", "💠", "🌀", "💤", "🏧", "♿️",
      "🅿️", "🚹", "🚺", "🚻", "🚮", "📶", "🔣", "ℹ️",
      "🔤", "🔡", "🔠", "🆗", "🆙", "🆒", "🆕", "🆓",
      "🔟", "🔢", "▶️", "⏸️", "⏹️", "⏺️", "⏭️", "⏮️",
      "🔀", "🔁", "🔄", "➡️", "⬅️", "⬆️", "⬇️", "↔️",
      "🔗", "➕", "➖", "➗", "✖️", "™️", "©️", "®️",
    ],
  },
  {
    key: "flags",
    label: "Flags",
    icon: "🏳️",
    emojis: [
      "🏳️", "🏴", "🏁", "🚩", "🇺🇸", "🇬🇧", "🇨🇦", "🇦🇺",
      "🇩🇪", "🇫🇷", "🇮🇹", "🇪🇸", "🇵🇹", "🇳🇱", "🇧🇪", "🇸🇪",
      "🇳🇴", "🇩🇰", "🇫🇮", "🇮🇪", "🇨🇭", "🇦🇹", "🇵🇱", "🇷🇺",
      "🇺🇦", "🇬🇷", "🇹🇷", "🇯🇵", "🇰🇷", "🇨🇳", "🇮🇳", "🇧🇷",
      "🇲🇽", "🇦🇷", "🇿🇦", "🇳🇬", "🇪🇬", "🇸🇦", "🇦🇪", "🇮🇱",
      "🇸🇬", "🇭🇰", "🇹🇭", "🇻🇳", "🇵🇭", "🇮🇩", "🇲🇾", "🇳🇿",
      "🇨🇱", "🇨🇴", "🇵🇪",
    ],
  },
];
